import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { Checkbox, Dropdown, IJSXOption, IOption } from "@evergis/ui";

import {
  Descr,
  UploadAttributeColumn,
  UploadAttributeInput,
  UploadAttributeType,
  UploadAttributeValue,
} from "../styled";

import { DateFormat } from "../../../../utils/date";
import { useUploadDataContext } from "../uploadDataContext";
import { fieldTypes } from "../../../../evergis/core/attributes/fieldTypes";
import { UploadAttributeProps, UploadFileAttribute } from "../types";

export const UploadAttribute: FC<UploadAttributeProps> = ({ name, type }) => {
  const { t } = useTranslation();
  const [fileAttribute, setFileAttribute] = useState<string>();

  const { dateOfImage, uploadedFiles, uploadAttributes, onChangeAttributeValue, onChangeAttributeCheckbox } =
    useUploadDataContext();

  const fileAttributeOptions: IOption[] = useMemo(
    () => [
      { text: t("uploadData.select", { ns: "dialogs" }), value: UploadFileAttribute.Default },
      { text: t("uploadData.rasterName", { ns: "dialogs" }), value: UploadFileAttribute.FileName },
      { text: t("uploadData.uploadDate", { ns: "dialogs" }), value: UploadFileAttribute.DateOfImage },
    ],
    [t],
  );

  const fileType = fieldTypes
    .map(value => ({
      ...value,
      text: value.langKey ? t(value.langKey, { ns: "common" }) : value.text,
    }))
    .find(item => item.value === type);

  const checked = Object.prototype.hasOwnProperty.call(uploadAttributes, name);
  const disabled = !!fileAttribute && (fileAttribute !== UploadFileAttribute.FileName || uploadedFiles.length > 1);

  const formatValue = useMemo(() => {
    switch (uploadAttributes[name]) {
      case UploadFileAttribute.FileName:
        return uploadedFiles.length === 1 ? uploadedFiles[0].name : "";
      case UploadFileAttribute.DateOfImage:
        return format(dateOfImage, DateFormat.ClientDefaultFormat);
      default:
        return uploadAttributes[name] || "";
    }
  }, [dateOfImage, name, uploadAttributes, uploadedFiles]);

  const onChangeFileAttribute = useCallback(
    (items: IJSXOption[]) => {
      setFileAttribute(items[0].value);
      onChangeAttributeValue(name, items[0].value);
    },
    [name, onChangeAttributeValue],
  );

  const onChangeCheckbox = useCallback(() => {
    onChangeAttributeCheckbox(name);
  }, [name, onChangeAttributeCheckbox]);

  const onChangeValue = useCallback(
    e => {
      onChangeAttributeValue(name, e.target.value);
    },
    [name, onChangeAttributeValue],
  );

  useEffect(() => {
    if (!checked) {
      setFileAttribute("");
    }
  }, [checked]);

  return (
    <>
      <div>
        <Checkbox checked={checked} onChange={onChangeCheckbox} />
      </div>
      <UploadAttributeColumn>
        <div>{name}</div>
        <Descr>{fileType?.text}</Descr>
      </UploadAttributeColumn>
      <UploadAttributeType>
        <Dropdown
          label={t("uploadData.fileAttribute", { ns: "dialogs" })}
          placeholder={t("uploadData.select", { ns: "dialogs" })}
          options={fileAttributeOptions}
          value={fileAttribute}
          onChange={onChangeFileAttribute}
          zIndex={10000}
        />
      </UploadAttributeType>
      <UploadAttributeValue>
        <UploadAttributeInput
          label={t("uploadData.value", { ns: "dialogs" })}
          value={formatValue}
          disabled={disabled}
          onChange={onChangeValue}
        />
      </UploadAttributeValue>
    </>
  );
};
